import { useEffect, useMemo, useState, useCallback } from 'react';
import { MdFastfood } from 'react-icons/md';
import { GiCook } from "react-icons/gi";
import { HiCurrencyDollar } from "react-icons/hi";
import { BiToggleLeft } from 'react-icons/bi';
import { useMenuData } from '../hooks/useMenuData';
import { useMenuSelection } from '../hooks/useMenuSelection';
import useFilters from '../../../components/reusables/filterBar/hooks/useFilters';
import { useAuth } from '../../../auth/AuthenticationWrapper';
import MenuList from './menuList';
import Pagination from '../../../components/lib/pagination';
import CentralModal from '../../../components/lib/centralModal';
import SpeedDial from '../../../components/lib/speedDial';
import BulkActionBar, { BulkActionButton } from '../../../components/reusables/bulkActions/bulkActionBar';
import FilterBar from '../../../components/reusables/filterBar/filterBar';
import RecipeRegisterModal from '../components/recipes/recipeRegisterModal';
import ComboRegisterModal from '../components/combos/comboRegisterModal';
import RecipeDetailsModalContent from '../components/recipes/recipeDetailsModal';
import ComboDetailsModalContent from '../components/combos/comboDetailsModal';
import ProductDetailsModalContent from '../../storage/components/productDetailsModal';
import BatchMenuStatusChangeModal from '../components/bulkActions/batchMenuStatusChangeModal';
import ClientService from '../../../rest/clientService';
import MenuGrid from './menuGrid';
import ViewToggle from '../../../components/lib/viewToggle';
import useViewMode from '../../../hooks/useViewMode';
import usePersistedState from '../../../hooks/usePersistedState';

function MenuTable() {
    const [pageSize, setPageSize] = useState(10);

    const [itemType, setItemType] = useState('Receita');

    const [sort, setSort] = usePersistedState(`menu:${itemType}:sort`, {
        field: '',
        direction: 'asc'
    });
    const [categoryOptions, setCategoryOptions] = useState([]);

    const [modals, setModals] = useState({
        recipe: false,
        combo: false,
        status: false
    });

    const toggleModal = useCallback((key, val) => {
        setModals(prev => ({ ...prev, [key]: val }));
    }, []);

    const [detailsItem, setDetailsItem] = useState(null);
    const { hasPermission, user } = useAuth();
    const canCreate = hasPermission("menu", "create");

    const role = sessionStorage.getItem('role');
    const isParent = role === 'PARENT';

    const [viewModePreference, setViewModePreference] =
        useViewMode('foodz_menu_view_mode', 'list');

    const currentViewMode = isParent ? 'grid' : viewModePreference;

    const {
        search, setSearch,
        filters, setFilters,
        updateFilter, clearFilters,
        page, setPage
    } = useFilters({}, `menu:${itemType}:filters`);

    const handleSortChange = (field) => {
        setSort(prev => {
            if (prev.field === field) {
                return {
                    field,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            return { field, direction: 'asc' };
        });
        setPage(0);
    };

    useEffect(() => {
        const loadPrefs = async () => {
            try {
                const prefs = await ClientService.getClientAdminPreferences();
                if (prefs?.categories) {
                    setCategoryOptions(
                        prefs.categories
                            .split(";")
                            .map(c => c.trim())
                            .filter(Boolean)
                            .map(c => ({ value: c, label: c }))
                    );
                }
            } catch (err) {
                console.error(err);
            }
        };
        loadPrefs();
    }, []);

    const filtersWithSearch = useMemo(() => {
        const activeFilters = { ...filters, search };
        if (isParent) activeFilters.status = true;
        return activeFilters;
    }, [filters, search, isParent]);

    const { pagedData, totalPages, totalElements, loading } =
        useMenuData(
            itemType,
            filtersWithSearch,
            page,
            pageSize,
            sort.field ? `${sort.field},${sort.direction}` : '',
            user
        );

    const selection = useMenuSelection(pagedData, itemType);

    const handleLoadChanging = (type) => {
        if (itemType !== type) {
            setItemType(type);
            selection.clearSelection();
        }
    };

    const handleBatchUpdate = async (isActive) => {
        if (selection.actions?.batchStatusUpdate) {
            await selection.actions.batchStatusUpdate(isActive);
            toggleModal('status', false);
        }
    };

    const getVisibleSelectedObjects = () => {
        if (!pagedData) return [];
        return pagedData.filter(item =>
            selection.selectedItems.includes(selection.getItemId(item))
        );
    };

    const filterConfigs = useMemo(() => {
        const configs = [];

        if (categoryOptions.length > 0) {
            configs.push({
                key: "categories",
                label: "Categorias",
                options: categoryOptions,
                placeholder: "Categoria",
                selectedValues: filters.categories || [],
                onChange: v => updateFilter("categories", v)
            });
        }

        if (itemType !== 'Produto') {
            configs.push({
                key: "status",
                label: "Status",
                placeholder: "Status",
                options: [
                    { value: true, label: "Ativo" },
                    { value: false, label: "Inativo" }
                ],
                selectedValues: filters.status || [],
                onChange: v => updateFilter("status", v)
            });
        }

        return configs;
    }, [filters, categoryOptions, itemType, updateFilter]);

    const rangeConfigs = useMemo(() => [{
        key: "price",
        label: "Faixa de Preço",
        min: filters.minPrice,
        max: filters.maxPrice,
        prefix: "R$",
        icon: HiCurrencyDollar,
        onChange: ({ min, max }) => {
            setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
            setPage(0);
        }
    }], [filters, setFilters, setPage]);

    return (
        <div>
            {/* FILTER / ACTION BAR (UNCHANGED) */}
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
                {!isParent && (
                    <>
                        <div className="flex-1 w-full z-10">
                            {selection.selectedItems.length > 0 && itemType !== 'Produto' ? (
                                <BulkActionBar
                                    selectedCount={selection.selectedItems.length}
                                    onClearSelection={selection.clearSelection}
                                >
                                    <BulkActionButton
                                        icon={BiToggleLeft}
                                        label="Ativar/Desativar"
                                        onClick={() => toggleModal('status', true)}
                                        variant="primary"
                                    />
                                </BulkActionBar>
                            ) : (
                                <FilterBar
                                    searchValue={search}
                                    onSearchChange={(val) => { setSearch(val); setPage(0); }}
                                    onResetPage={() => setPage(0)}
                                    placeholder={`Pesquisar ${itemType}...`}
                                    filters={filterConfigs}
                                    rangeFilters={rangeConfigs}
                                    onClearFilters={clearFilters}
                                    loading={loading}
                                />
                            )}
                        </div>

                        <div className="flex items-center gap-3 w-full lg:w-auto justify-end shrink-0 h-10">
                            <select
                                className='h-full pl-3 pr-8 border border-slate-300 rounded-lg bg-white text-sm outline-none'
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                            >
                                {[10, 20, 30, 50].map(s =>
                                    <option key={s} value={s}>{s} por pág</option>
                                )}
                            </select>

                            <ViewToggle
                                view={currentViewMode}
                                onChange={setViewModePreference}
                            />

                            <SpeedDial
                                disabled={!canCreate}
                                label="Adicionar"
                                buttonType="confirm"
                                actions={[
                                    { label: "Receita", icon: GiCook, onClick: () => toggleModal('recipe', true) },
                                    { label: "Combo", icon: MdFastfood, onClick: () => toggleModal('combo', true) },
                                ]}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* ✅ TABS PRESERVED */}
            <table className="table-fixed w-full">
                <thead>
                    <tr className="text-white h-10 font-montserrat text-md">
                        {['Receita', 'Produto', 'Combo'].map(type => (
                            <th
                                key={type}
                                className={`rounded-t-lg border-2 border-white ${
                                    itemType === type
                                        ? "bg-custom_blue"
                                        : "bg-custom_blue/50"
                                } cursor-pointer hover:bg-custom_blue transition-colors`}
                                onClick={() => handleLoadChanging(type)}
                            >
                                {type}
                            </th>
                        ))}
                    </tr>
                </thead>
            </table>

            {/* VIEW SWITCH */}
            {loading ? (
                <div className="w-full py-20 text-center bg-white border-x-2 border-b-2 text-gray-400 animate-pulse">
                    Carregando cardápio...
                </div>
            ) : currentViewMode === 'list' ? (
                <MenuList
                    data={pagedData}
                    itemType={itemType}
                    selectedItems={selection.selectedItems}
                    onSelectAll={selection.handleSelectAll}
                    onSelectItem={selection.handleSelectItem}
                    onSortChange={handleSortChange}
                    sortField={sort.field}
                    sortDirection={sort.direction}
                    onOpenDetails={setDetailsItem}
                    getItemId={selection.getItemId}
                    showSelection={!isParent}
                    showQuantity={!isParent}
                />
            ) : (
                <MenuGrid
                    data={pagedData}
                    itemType={itemType}
                    onOpenDetails={setDetailsItem}
                    showSelection={!isParent}
                    showQuantity={!isParent}
                />
            )}

            <div className="py-4">
                {totalPages > 1 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={totalElements}
                        pageSize={pageSize}
                    />
                )}
            </div>

            {/* MODALS (UNCHANGED) */}
            <RecipeRegisterModal
                isOpen={modals.recipe}
                setIsOpen={(v) => toggleModal('recipe', v)}
            />

            <ComboRegisterModal
                isOpen={modals.combo}
                setIsOpen={(v) => toggleModal('combo', v)}
            />

            <CentralModal
                isOpen={!!detailsItem}
                onClose={() => setDetailsItem(null)}
                title={`Detalhes ${
                    itemType === 'Receita'
                        ? 'da Receita'
                        : itemType === 'Combo'
                            ? 'do Combo'
                            : 'do Produto'
                }`}
                className="md:max-w-lg lg:max-w-3xl xl:max-w-5xl md:px-4 md:-mr-16"
            >
                {detailsItem && itemType === 'Receita' && (
                    <RecipeDetailsModalContent recipe={detailsItem} />
                )}
                {detailsItem && itemType === 'Combo' && (
                    <ComboDetailsModalContent combo={detailsItem} />
                )}
                {detailsItem && itemType === 'Produto' && (
                    <ProductDetailsModalContent product={detailsItem} />
                )}
            </CentralModal>

            <BatchMenuStatusChangeModal
                isOpen={modals.status}
                onClose={() => toggleModal('status', false)}
                selectedItems={getVisibleSelectedObjects()}
                itemType={itemType}
                onConfirm={handleBatchUpdate}
            />
        </div>
    );
}

export default MenuTable;